'use client';

/**
 * КТО ПЕРЕЗАГРУЗИЛ ВКЛАДКУ — МЫ ИЛИ БРАУЗЕР.
 *
 * ── Зачем ────────────────────────────────────────────────────────────────
 * Жалоба «доска на телефоне через пару минут начинает перезагружаться, потом
 * Сайт недоступен» неразличима снаружи: перезагрузку могли сделать и мы сами
 * (откат состояния, протухший чанк, восстановление версии), и браузер, убивший
 * вкладку по памяти. Лечение у этих случаев противоположное, а в агрегаторе
 * ошибок не видно ни того, ни другого: упавшая вкладка сообщить не успевает, а
 * наша перезагрузка ошибкой не считается.
 *
 * Разбор 2026-09-09 упёрся ровно в это: 12 загрузок с мобильного UA, все с
 * кодом 200, в GlitchTip тишина, и понять причину было нечем.
 *
 * ── Как различаем ────────────────────────────────────────────────────────
 * Перед КАЖДОЙ своей перезагрузкой оставляем расписку (`markOurReload`).
 * На следующей загрузке смотрим тип навигации: `reload` без свежей расписки =
 * перезагрузил не наш код. Расписка живёт секунды: старая относится к прошлому
 * случаю и оправданием служить не может.
 *
 * ── Чего здесь СОЗНАТЕЛЬНО нет ───────────────────────────────────────────
 * Идентификатора доски. Адрес публичной доски — сам по себе ключ доступа
 * (48 hex в пути), и отправлять его в агрегатор ошибок значило бы раздавать
 * доступ к чужой доске всем, у кого есть доступ к GlitchTip.
 */

/** Ключ расписки: кто и когда затеял перезагрузку. */
export const RELOAD_MARK_KEY = '__pronaReloadBy';
/** Ключ отметки времени загрузки — из него считаем, сколько прожила вкладка. */
export const PAGE_LOAD_AT_KEY = '__pronaPageLoadAt';
/**
 * Ключ БОРТОВОГО ЖУРНАЛА: что было на борту в последний снятый момент.
 *
 * Без него отчёт бесполезен. Первая версия дознания снимала цифры в момент
 * монтирования НОВОЙ страницы — там канв ещё нет, и в агрегатор уезжали нули
 * (`canvases: 0`, `snapshots: -1`), то есть ровно ничего о той жизни, которая
 * оборвалась. Теперь страница пишет замер раз в несколько секунд, и после
 * смерти мы докладываем ПОСЛЕДНИЙ замер прошлой жизни.
 */
export const LIFE_SAMPLE_KEY = '__pronaLifeSample';
/**
 * Ключ отметки ШТАТНОГО УХОДА (`pagehide`).
 *
 * Единственное, чем ручная перезагрузка отличается от снятия процесса: когда
 * человек жмёт «обновить» или уходит по ссылке, браузер даёт странице отработать
 * обработчики. Когда систему прижало по памяти — их не зовут вовсе. Без этого
 * различения каждое нажатие «обновить» приезжало как «вкладку убили», и
 * настоящий случай тонул в шуме (разбор 2026-09-10).
 */
export const CLEAN_EXIT_KEY = '__pronaCleanExit';
/** Расписка старше этого срока — не про текущую перезагрузку. */
export const MARK_FRESH_MS = 30_000;
/** Замер старше этого срока — из прошлого сеанса, а не из только что умершей жизни. */
export const SAMPLE_FRESH_MS = 120_000;
/**
 * Ключ ОЧЕРЕДИ НЕОТПРАВЛЕННЫХ отчётов.
 *
 * Отчёт о смерти отправляет следующая жизнь страницы — и на телефоне она сама
 * может не дожить: замеры 2026-09-10 показали жизни по 4 и 6 секунд, а
 * отправка (динамический импорт SDK плюс запрос по сети) столько и занимает.
 * Из-за этого терялся самый нужный отчёт — тот, что описывает смерть на зуме, —
 * а доезжали только отчёты о коротких жизнях в петле перезагрузок.
 *
 * Поэтому отчёт сперва КЛАДЁТСЯ В ОЧЕРЕДЬ и снимается оттуда только после
 * подтверждённой отправки. Какая-нибудь из жизней проживёт достаточно и вынесет
 * всё накопленное.
 */
export const PENDING_REPORTS_KEY = '__pronaPendingReports';
/** Больше держать незачем: очередь нужна на пару кругов, а не для истории. */
export const MAX_PENDING_REPORTS = 5;

export type ReloadStorage = Pick<Storage, 'getItem' | 'setItem'>;

/**
 * Кто оборвал прошлую жизнь вкладки.
 *  • `наша` — наш код (откат, протухший чанк, восстановление версии);
 *  • `ручная` — человек обновил или ушёл: страница успела отработать pagehide;
 *  • `убита` — обработчики не звались вовсе, процесс сняли (память/система).
 */
export type ReloadVerdict = 'первая-загрузка' | 'наша' | 'ручная' | 'убита';

/** Что было на борту вкладки в момент замера. */
export interface LifeSample {
  ts: number;
  /** Мегабайты канв и их число. */
  canvasMb: number;
  canvases: number;
  /** Узлов DOM — растёт, когда на доске разворачиваются живые тела. */
  domNodes: number;
  /** Счётчики битмап-подмены. */
  snapshots: number;
  strips: number;
  /** Зум доски в момент замера — привязка к жесту, на котором всё умирает. */
  zoom: number;
  /** Сколько мс вкладка провела скрытой. */
  hiddenMs: number;
}

export interface ReloadReport {
  verdict: ReloadVerdict;
  /** Причина нашей перезагрузки — как её назвал вызывающий код. */
  reason?: string;
  /** Сколько прожила прошлая жизнь вкладки, мс (если знаем). */
  previousLifeMs?: number;
  /** Последний бортовой замер ПРОШЛОЙ жизни — то, ради чего всё и затевалось. */
  lastSample?: LifeSample;
}

function safeStorage(): ReloadStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null; // приватный режим с запретом хранилища — дознание молчит
  }
}

/**
 * Расписка «перезагружаемся мы, вот почему». Зовётся ПЕРЕД `location.reload()`.
 * Отказ хранилища проглатываем: перезагрузка важнее дознания.
 */
export function markOurReload(
  reason: string,
  storage: ReloadStorage | null = safeStorage(),
  now: number = Date.now(),
): void {
  try {
    storage?.setItem(RELOAD_MARK_KEY, JSON.stringify({ reason, ts: now }));
  } catch {
    /* нет места/нет хранилища */
  }
}

/**
 * Разобрать, кто перезагрузил вкладку.
 *
 * `navType` — `performance.getEntriesByType('navigation')[0].type`. Чистая
 * функция: всё внешнее приходит параметрами, поэтому проверяется без браузера.
 */
export function classifyReload(
  navType: string | undefined,
  rawMark: string | null,
  now: number,
  previousLoadAt?: number | null,
  rawCleanExit?: string | null,
  lastSample?: LifeSample | null,
): ReloadReport {
  const previousLifeMs =
    typeof previousLoadAt === 'number' && Number.isFinite(previousLoadAt) && previousLoadAt <= now
      ? now - previousLoadAt
      : undefined;
  const sample = lastSample && now - lastSample.ts < SAMPLE_FRESH_MS ? lastSample : undefined;
  const base = { previousLifeMs, lastSample: sample };

  const mark = parseStamp(rawMark);
  if (mark && Number.isFinite(mark.ts) && now - mark.ts >= 0 && now - mark.ts < MARK_FRESH_MS) {
    return { verdict: 'наша', reason: mark.reason ?? 'без причины', ...base };
  }
  /*
   * НА ТИП НАВИГАЦИИ НЕ ПОЛАГАЕМСЯ.
   *
   * После снятия процесса браузер иногда открывает страницу как `navigate`, а
   * не `reload` — так было у пользователя, когда встроенный браузер перезапустил
   * приложение целиком, и дознание молча промолчало. Решает другое: остался ли
   * СВЕЖИЙ бортовой замер. Он есть только если несколько секунд назад в этой же
   * вкладке работала другая жизнь страницы — а значит, она оборвалась.
   */
  if (!sample) return { verdict: 'первая-загрузка', ...base };
  const clean = parseStamp(rawCleanExit);
  if (clean && Number.isFinite(clean.ts) && now - clean.ts >= 0 && now - clean.ts < MARK_FRESH_MS) {
    return { verdict: 'ручная', ...base };
  }
  return { verdict: 'убита', ...base };
}

/** Разобрать отметку `{reason?, ts}`; битая или пустая — `null`. */
function parseStamp(raw: string | null | undefined): { reason?: string; ts: number } | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as { reason?: unknown; ts?: unknown };
    if (typeof v?.ts !== 'number') return null;
    return { reason: typeof v.reason === 'string' ? v.reason : undefined, ts: v.ts };
  } catch {
    return null;
  }
}

/** Отметить штатный уход страницы — зовётся на `pagehide`. */
export function markCleanExit(
  storage: ReloadStorage | null = safeStorage(),
  now: number = Date.now(),
): void {
  try {
    storage?.setItem(CLEAN_EXIT_KEY, JSON.stringify({ ts: now }));
  } catch {
    /* нет хранилища — вердикт будет осторожнее, и только */
  }
}

/** Записать бортовой замер текущей жизни вкладки. */
export function recordLifeSample(
  sample: Omit<LifeSample, 'ts'>,
  storage: ReloadStorage | null = safeStorage(),
  now: number = Date.now(),
): void {
  try {
    storage?.setItem(LIFE_SAMPLE_KEY, JSON.stringify({ ...sample, ts: now } satisfies LifeSample));
  } catch {
    /* приватный режим/квота */
  }
}

/**
 * Снять показания текущей загрузки и записать отметку времени для следующей.
 * Возвращает `null`, если хранилища нет: без него различить случаи нельзя, и
 * выдумывать вердикт вредно — он попадёт в агрегатор как факт.
 */
export function readReloadReport(
  navType: string | undefined,
  storage: ReloadStorage | null = safeStorage(),
  now: number = Date.now(),
): ReloadReport | null {
  if (!storage) return null;
  let previousLoadAt: number | null = null;
  try {
    const raw = storage.getItem(PAGE_LOAD_AT_KEY);
    const parsed = raw ? Number.parseInt(raw, 10) : Number.NaN;
    previousLoadAt = Number.isFinite(parsed) ? parsed : null;
  } catch {
    previousLoadAt = null;
  }
  let lastSample: LifeSample | null = null;
  try {
    const raw = safeGet(storage, LIFE_SAMPLE_KEY);
    const parsed = raw ? (JSON.parse(raw) as LifeSample) : null;
    if (parsed && typeof parsed.ts === 'number') lastSample = parsed;
  } catch {
    lastSample = null;
  }
  const report = classifyReload(
    navType,
    safeGet(storage, RELOAD_MARK_KEY),
    now,
    previousLoadAt,
    safeGet(storage, CLEAN_EXIT_KEY),
    lastSample,
  );
  try {
    storage.setItem(PAGE_LOAD_AT_KEY, String(now));
    // Расписку и отметку ухода гасим сразу: каждая объясняет ровно один случай.
    storage.setItem(RELOAD_MARK_KEY, '');
    storage.setItem(CLEAN_EXIT_KEY, '');
    storage.setItem(LIFE_SAMPLE_KEY, '');
  } catch {
    /* не смогли — вердикт следующей загрузки будет осторожнее, и только */
  }
  return report;
}

function safeGet(storage: ReloadStorage, key: string): string | null {
  try {
    const v = storage.getItem(key);
    return v ? v : null;
  } catch {
    return null;
  }
}

/**
 * Положить отчёт в очередь на отправку. Возвращает всю очередь — вызывающий
 * отправляет её целиком и, только дождавшись подтверждения, зовёт
 * `clearPendingReports`.
 */
export function queueReport(
  report: ReloadReport,
  storage: ReloadStorage | null = safeStorage(),
): ReloadReport[] {
  const all = readPendingReports(storage);
  all.push(report);
  const kept = all.slice(-MAX_PENDING_REPORTS);
  try {
    storage?.setItem(PENDING_REPORTS_KEY, JSON.stringify(kept));
  } catch {
    /* не смогли отложить — отправим то, что держим в руках */
  }
  return kept;
}

/** Прочитать очередь; битое содержимое считаем пустой очередью. */
export function readPendingReports(storage: ReloadStorage | null = safeStorage()): ReloadReport[] {
  try {
    const raw = storage?.getItem(PENDING_REPORTS_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return Array.isArray(parsed) ? (parsed as ReloadReport[]) : [];
  } catch {
    return [];
  }
}

/** Снять очередь — ТОЛЬКО после подтверждённой отправки. */
export function clearPendingReports(storage: ReloadStorage | null = safeStorage()): void {
  try {
    storage?.setItem(PENDING_REPORTS_KEY, '');
  } catch {
    /* останется на следующий круг — дубль лучше потери */
  }
}
